(ns ui.gallery.drag
  "The drag & drop state of the Replicant gallery as pure functions over
   the drag map (the `:drag` entry of the gallery state).

   Browsers only expose the `dataTransfer` payload on the final drop
   event, so drop targets cannot inspect it while the drag is still in
   flight. The drag map therefore tracks what is being dragged (gallery
   entries or a folder), so that drop targets can render visual
   feedback and reject invalid targets during `dragover`:

   - `:drag/kind` `:media` or `:folder`
   - `:story-media/eids` the dragged entries, `:folder/uuid` the dragged folder
   - `:drag/source-folder` the uuid of the folder the drag started in
   - `:drag/over` the hovered drop target and `:drag/entered-at` when the
     drag entered it (for the spring-load delay)")

(def spring-load-delay-ms
  "How long a drag has to hover over a folder before it springs open."
  700)

(def moving-out-ms
  "How long entries stay marked as moving out after a move."
  4000)

(defn active?
  "Is a gallery drag in flight?"
  [drag]
  (some? drag))

(defn dragged-media-eid?
  "Is the gallery entry `eid` part of the drag in flight?"
  [drag eid]
  (and (= :media (:drag/kind drag))
       (contains? (:story-media/eids drag) eid)))

(defn dragged-folder?
  "Is the folder `folder-uuid` the drag source of the drag in flight?"
  [drag folder-uuid]
  (and (= :folder (:drag/kind drag))
       (= folder-uuid (:folder/uuid drag))))

(defn valid-drop-target?
  "Is `folder-uuid` a valid drop target for the drag in flight? False
   when no gallery drag is active (e.g. files dragged in from the OS)."
  [drag folder-uuid]
  (case (:drag/kind drag)
    :media true
    :folder (not= folder-uuid (:folder/uuid drag))
    false))

(defn valid-current-folder-target?
  "Is the current folder `folder-uuid` a valid drop target for the drag
   in flight? Only once the drag left the folder it started in (via a
   folder that sprang open, or the folder chain), since dropping entries
   into the folder they already live in would be a no-op."
  [drag folder-uuid]
  (and (valid-drop-target? drag folder-uuid)
       (not= folder-uuid (:drag/source-folder drag))))

(defn valid-target?
  "Dispatches on the drop target `opts`: a current-folder drop zone
   (`:current-folder? true`) or a folder row / chain entry."
  [drag folder-uuid opts]
  (if (:current-folder? opts)
    (valid-current-folder-target? drag folder-uuid)
    (valid-drop-target? drag folder-uuid)))

(defn set-over
  "Marks `folder-uuid` as the hovered drop target. Keeps the original
   enter timestamp while the drag moves between the children of the
   same target, so the spring-load timer does not reset."
  [drag folder-uuid timestamp]
  (if (= folder-uuid (:drag/over drag))
    drag
    (assoc drag
           :drag/over folder-uuid
           :drag/entered-at timestamp)))

(defn clear-over
  [drag folder-uuid]
  (if (= folder-uuid (:drag/over drag))
    (dissoc drag :drag/over :drag/entered-at)
    drag))

(defn over?
  [drag folder-uuid]
  (and (some? drag)
       (= folder-uuid (:drag/over drag))))

(defn spring-load-elapsed?
  "True once the drag hovered over `folder-uuid` for the spring-load
   delay, meaning the folder should be opened."
  [drag folder-uuid timestamp]
  (and (over? drag folder-uuid)
       (number? (:drag/entered-at drag))
       (< spring-load-delay-ms
          (- timestamp (:drag/entered-at drag)))))

(defn mark-moving-out
  "Marks gallery entries (by eid) or folders (by uuid) as animating out
   after a move, so they fade out instead of suddenly disappearing with
   the next data refresh."
  [gallery keys*]
  (update gallery
          :moving-out
          (fnil into #{})
          keys*))

(defn unmark-moving-out
  [gallery keys*]
  (update gallery
          :moving-out
          (fn [moving-out]
            (reduce disj
                    (or moving-out #{})
                    keys*))))

(defn moving-out?
  [gallery key*]
  (contains? (:moving-out gallery) key*))
